import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../context/AuthContext';
import {
  BarChart3,
  Book,
  ChevronDown,
  Database,
  Home,
  Key,
  Layers,
  LogOut,
  Settings,
  Shield,
  Users
} from 'lucide-react';
import {toast} from 'react-toastify';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAdminDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Documentation', href: '/docs', icon: Book },
  ];

  const userNavigation = isAuthenticated ? [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    { name: 'API Keys', href: '/api-keys', icon: Key },
  ] : [];

  const adminDropdownItems = [
    { name: 'Dashboard', href: '/admin', icon: BarChart3 },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'API Keys', href: '/admin/api-keys', icon: Key },
    { name: 'Subscriptions', href: '/admin/subscriptions', icon: Settings },
      {name: 'TCG Data', href: '/admin/tcg-data', icon: Layers},
    { name: 'TCG Config', href: '/admin/tcg-configs', icon: Database },
    { name: 'Blocked IPs', href: '/admin/blocked-ips', icon: Shield },
  ];

  const allNavigation = [...navigation, ...userNavigation];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center">
                  <img src="/logo.svg" alt="TCG API" className="h-10" />
                </Link>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {allNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/' && location.pathname.startsWith(item.href));
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? 'border-blue-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* Admin Dropdown */}
                {isAdmin && (
                  <div className="relative inline-flex items-center" ref={dropdownRef}>
                    <button
                      onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                      className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                        location.pathname.startsWith('/admin')
                          ? 'border-red-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </button>

                    {showAdminDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {adminDropdownItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.href;

                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setShowAdminDropdown(false)}
                                className={`flex items-center px-4 py-2 text-sm transition-colors ${
                                  isActive
                                    ? 'bg-red-50 text-red-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                <Icon className="w-4 h-4 mr-3" />
                                {item.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-700">
                    Welcome, {user.username}
                    {isAdmin && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Admin
                      </span>
                    )}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="btn btn-sm btn-ghost"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary text-sm"
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              © 2025 TCG API. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/docs" className="text-sm text-gray-500 hover:text-gray-700">
                Documentation
              </Link>
              <Link to="/sitemap" className="text-sm text-gray-500 hover:text-gray-700">
                Sitemap
              </Link>
              <a
                href="/health"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Status
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;